import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import ConfirmModal from '../../components/ConfirmModal';
import {api} from '../../api';


const STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-800',
  POSTED: 'bg-green-100 text-green-800',
  VOID: 'bg-red-100 text-red-800',
};

export default function JournalEntryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  useEffect(() => {
    api(`/journal-entries/${id}`).then(r => r.json()).then(setEntry);
  }, [id]);

  const handleDelete = async () => {
    await api(`/journal-entries/${id}`, { method: 'DELETE' });
    navigate('/accounting/journal-entries');
  };

  if (!entry) return null;

  const totalDebit = (entry.lines || []).reduce((s, l) => s + (l.debit || 0), 0);
  const totalCredit = (entry.lines || []).reduce((s, l) => s + (l.credit || 0), 0);

  return (
    <Layout>
      <Link to="/accounting/journal-entries" className="text-emerald-600 hover:text-emerald-800 mb-4 inline-block">&larr; Back to Journal Entries</Link>
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{entry.reference || 'Journal Entry'}</h1>
              <p className="text-gray-600">{entry.entryDate}{entry.description ? ` — ${entry.description}` : ''}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${STATUS_COLORS[entry.status]}`}>{entry.status}</span>
              <Link to={`/accounting/journal-entries/${id}/edit`} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Edit</Link>
              <button onClick={() => setDeleteModal(true)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50">Delete</button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Description</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(entry.lines || []).map((line) => (
                <tr key={line.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-mono text-sm text-gray-500">{line.accountCode}</span> {line.accountName}
                  </td>
                  <td className="px-6 py-4 text-gray-600 hidden md:table-cell">{line.description || '—'}</td>
                  <td className="px-6 py-4 text-right font-mono">{line.debit > 0 ? line.debit.toFixed(2) : ''}</td>
                  <td className="px-6 py-4 text-right font-mono">{line.credit > 0 ? line.credit.toFixed(2) : ''}</td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-semibold">
                <td colSpan={2} className="px-6 py-3 text-right">Totals</td>
                <td className="px-6 py-3 text-right font-mono">{totalDebit.toFixed(2)}</td>
                <td className="px-6 py-3 text-right font-mono">{totalCredit.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal open={deleteModal} title="Delete Journal Entry" message="Are you sure you want to delete this journal entry?" onConfirm={handleDelete} onCancel={() => setDeleteModal(false)} />
    </Layout>
  );
}
